

import React, { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { ExpenseEntry } from '../types';
import { API_BASE_URL } from '../constants';


interface ExpenseContextType {
  allExpenses: ExpenseEntry[];
  loading: boolean;
  saveExpense: (expenseData: Partial<ExpenseEntry> | Partial<ExpenseEntry>[]) => Promise<{ success: boolean; message?: string }>;
  deleteExpense: (expenseId: string) => Promise<void>;
  deleteExpenses: (expenseIds: string[]) => Promise<void>;
  deleteExpensesByProjectId: (projectId: string) => Promise<void>; // This should not be needed with CASCADE DELETE
  refreshExpenses: () => Promise<void>;
}

export const ExpenseContext = createContext<ExpenseContextType | undefined>(undefined);

interface ExpenseProviderProps {
  children: ReactNode;
}

const computeStepDateAndMonth = (startDateStr: string | undefined, startMonthStr: string | undefined, stepIndex: number) => {
    let baseYear: number;
    let baseMonth: number;
    let baseDay: number;

    if (startDateStr && startDateStr.length >= 10) {
        const parts = startDateStr.slice(0, 10).split('-');
        baseYear = parseInt(parts[0], 10);
        baseMonth = parseInt(parts[1], 10);
        baseDay = parseInt(parts[2], 10);
    } else if (startMonthStr && startMonthStr.length >= 7) {
        const parts = startMonthStr.slice(0, 7).split('-');
        baseYear = parseInt(parts[0], 10);
        baseMonth = parseInt(parts[1], 10);
        baseDay = 15;
    } else {
        const now = new Date();
        baseYear = now.getFullYear();
        baseMonth = now.getMonth() + 1;
        baseDay = 15;
    }

    if (isNaN(baseYear) || isNaN(baseMonth) || isNaN(baseDay)) {
        const now = new Date();
        baseYear = now.getFullYear();
        baseMonth = now.getMonth() + 1;
        baseDay = 15;
    }

    const totalMonthIndex = (baseMonth - 1) + stepIndex;
    const targetYear = baseYear + Math.floor(totalMonthIndex / 12);
    const targetMonth = ((totalMonthIndex % 12) + 12) % 12 + 1;

    const daysInTargetMonth = new Date(targetYear, targetMonth, 0).getDate();
    const targetDay = Math.min(baseDay, daysInTargetMonth);

    const targetMonthStr = `${targetYear}-${String(targetMonth).padStart(2, '0')}`;
    const targetDateStr = `${targetMonthStr}-${String(targetDay).padStart(2, '0')}`;

    return { month: targetMonthStr, date: targetDateStr };
};

export const ExpenseProvider: React.FC<ExpenseProviderProps> = ({ children }) => {
    const [allExpenses, setAllExpenses] = useState<ExpenseEntry[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchExpenses = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/expenses`);
            if (!response.ok) throw new Error('Failed to fetch expenses');
            const data: ExpenseEntry[] = await response.json();
            setAllExpenses(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchExpenses();
    }, [fetchExpenses]);

    const saveExpense = useCallback(async (expenseData: Partial<ExpenseEntry> | Partial<ExpenseEntry>[]): Promise<{ success: boolean; message?: string }> => {
        // Prepare headers with user identification for history auditing
        const userStr = localStorage.getItem('lamCustosUser');
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (userStr) {
            try {
                const parsed = JSON.parse(userStr);
                if (parsed.email) headers['X-User-Email'] = parsed.email;
                if (parsed.name) headers['X-User-Name'] = encodeURIComponent(parsed.name);
            } catch (e) {
                console.error("Erro ao carregar usuário para cabeçalho:", e);
            }
        }

        // Handle single expense update (PUT)
        if (!Array.isArray(expenseData) && expenseData.id) {
            try {
                const response = await fetch(`${API_BASE_URL}/expenses/${expenseData.id}`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify(expenseData),
                });
                const savedData = await response.json();

                if (!response.ok) {
                    return { success: false, message: savedData.message || 'Falha ao atualizar despesa.' };
                }

                setAllExpenses(current => current.map(e => e.id === savedData.id ? savedData : e));
                return { success: true };
            } catch (error) {
                console.error("Erro ao atualizar despesa:", error);
                 if (error instanceof SyntaxError) {
                    return { 
                        success: false, 
                        message: 'Falha de comunicação com o servidor. Isso geralmente acontece se um anexo for muito grande. Tente um arquivo menor.' 
                    };
                }
                return { success: false, message: (error as Error).message || 'Erro de conexão.' };
            }
        }

        // Handle expense creation (POST), both single and bulk
        let expensesToCreate: Partial<ExpenseEntry>[];

        if (Array.isArray(expenseData)) {
            // Bulk import from ImportacaoPage
            expensesToCreate = expenseData;
        } else {
            // Single expense creation, potentially recurring
            if (expenseData.isRecurring && expenseData.recurrenceMonths && expenseData.recurrenceMonths > 0) {
                // Generate recurring entries
                expensesToCreate = [];
                const totalMonthsToRepeat = expenseData.recurrenceMonths;
                const seriesId = `series_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

                for (let i = 0; i < totalMonthsToRepeat + 1; i++) {
                    const step = computeStepDateAndMonth(expenseData.date, expenseData.month, i);

                    const newExpense: Partial<ExpenseEntry> = {
                        ...expenseData,
                        id: `exp_${Date.now()}_${Math.random()}`,
                        month: step.month,
                        date: step.date,
                        description: `${expenseData.description || ''} (Parcela ${i + 1}/${totalMonthsToRepeat + 1})`.trim(),
                        isRecurring: true,
                        recurrenceMonths: totalMonthsToRepeat,
                        seriesId,
                    };
                    expensesToCreate.push(newExpense);
                }
            } else if (expenseData.isInstallment && expenseData.installmentMonths && expenseData.installmentMonths > 0) {
                // Generate installment entries
                expensesToCreate = [];
                const totalInstallments = expenseData.installmentMonths;
                const totalAmount = expenseData.amount || 0;
                const installmentAmount = totalAmount / totalInstallments;
                const seriesId = `series_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

                for (let i = 0; i < totalInstallments; i++) {
                    const step = computeStepDateAndMonth(expenseData.date, expenseData.month, i);

                    const newExpense: Partial<ExpenseEntry> = {
                        ...expenseData,
                        id: `exp_${Date.now()}_${Math.random()}`,
                        month: step.month,
                        date: step.date,
                        amount: installmentAmount,
                        description: `${expenseData.description || ''} (Parcela ${i + 1}/${totalInstallments})`.trim(),
                        isInstallment: true,
                        installmentMonths: totalInstallments,
                        seriesId,
                    };
                    expensesToCreate.push(newExpense);
                }
            } else {
                // Standard single creation
                expensesToCreate = [expenseData];
            }
        }
        
        if (expensesToCreate.length === 0) {
            return { success: true }; // Nothing to do
        }

        try {
            const response = await fetch(`${API_BASE_URL}/expenses`, {
                method: 'POST',
                headers,
                body: JSON.stringify(expensesToCreate),
            });
            const savedExpenses = await response.json();

            if (!response.ok) {
                return { success: false, message: savedExpenses.message || 'Falha ao adicionar despesa(s).' };
            }

            // The backend returns an array for POST, even for a single item.
            setAllExpenses(current => [...savedExpenses, ...current]);
            return { success: true };
        } catch (error) {
            console.error("Erro ao salvar despesa(s):", error);
            if (error instanceof SyntaxError) {
                return { 
                    success: false, 
                    message: 'Falha de comunicação com o servidor. Isso geralmente acontece se um anexo for muito grande. Tente um arquivo menor.' 
                };
            }
            return { success: false, message: (error as Error).message || 'Erro de conexão.' };
        }
    }, []);

    const deleteExpense = useCallback(async (expenseId: string) => {
        // Prepare headers with user identification for history auditing
        const userStr = localStorage.getItem('lamCustosUser');
        const headers: Record<string, string> = {};
        if (userStr) {
            try {
                const parsed = JSON.parse(userStr);
                if (parsed.email) headers['X-User-Email'] = parsed.email;
                if (parsed.name) headers['X-User-Name'] = encodeURIComponent(parsed.name);
            } catch (e) {
                console.error(e);
            }
        }

        try {
            const response = await fetch(`${API_BASE_URL}/expenses/${expenseId}`, {
                method: 'DELETE',
                headers,
            });
            if (!response.ok) throw new Error('Failed to delete expense');
            const result = await response.json();
            if (result.success) {
                setAllExpenses(current => current.filter(e => e.id !== expenseId));
            }
        } catch (error) {
            console.error(error);
        }
    }, []);

    const deleteExpenses = useCallback(async (expenseIds: string[]) => {
        if (!expenseIds || expenseIds.length === 0) return;
        // Prepare headers with user identification for history auditing
        const userStr = localStorage.getItem('lamCustosUser');
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (userStr) {
            try {
                const parsed = JSON.parse(userStr);
                if (parsed.email) headers['X-User-Email'] = parsed.email;
                if (parsed.name) headers['X-User-Name'] = encodeURIComponent(parsed.name);
            } catch (e) {
                console.error(e);
            }
        }

        try {
            const response = await fetch(`${API_BASE_URL}/expenses/delete-bulk`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ ids: expenseIds }),
            });
            if (!response.ok) throw new Error('Failed to delete bulk expenses');
            const result = await response.json();
            if (result.success) {
                setAllExpenses(current => current.filter(e => !expenseIds.includes(e.id)));
            }
        } catch (error) {
            console.error(error);
        }
    }, []);
    
    // This function becomes less critical with ON DELETE CASCADE in the DB,
    // but can be kept for frontend state consistency if needed.
    const deleteExpensesByProjectId = useCallback(async (projectId: string) => {
        setAllExpenses(current => current.filter(e => e.projectId !== projectId));
    }, []);

    return (
        <ExpenseContext.Provider value={{ allExpenses, loading, saveExpense, deleteExpense, deleteExpenses, deleteExpensesByProjectId, refreshExpenses: fetchExpenses }}>
            {children}
        </ExpenseContext.Provider>
    );
};